// Add Account team member modal — Accounts-Teams-01.
//
// Uses the Settings-01 Team Members list as the candidate set so
// the picker shares one inventory across the app. Suspended /
// deactivated members are filtered out client-side; pending invites
// (status=invited) are filtered too because the backend rejects
// them anyway. Already-assigned members render disabled so the
// broker sees who's already on the team.

import { useMemo, useState } from "react";

import { Button, Modal } from "@/design-system";
import {
  TeamMember,
  useTeamMembersQuery,
} from "@/features/settings";


export interface AddAccountTeamMemberModalProps {
  open: boolean;
  onClose: () => void;
  /** Membership ids already assigned (active rows). The picker
   * disables these so the broker sees the existing team without
   * the modal swallowing duplicate-add attempts silently. */
  alreadyAssignedMembershipIds: string[];
  /** True when someone else currently holds Account Lead. The
   * helper copy under the toggle warns the broker that the prior
   * lead will be replaced. */
  hasExistingLead: boolean;
  saving: boolean;
  error: string | null;
  onSubmit: (input: {
    membership_id: string;
    is_account_lead: boolean;
  }) => void;
}


export function AddAccountTeamMemberModal({
  open, onClose, alreadyAssignedMembershipIds, hasExistingLead,
  saving, error, onSubmit,
}: AddAccountTeamMemberModalProps) {
  const teamQuery = useTeamMembersQuery();
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLead, setIsLead] = useState(false);

  const assignedSet = useMemo(
    () => new Set(alreadyAssignedMembershipIds),
    [alreadyAssignedMembershipIds],
  );

  const candidates = useMemo<TeamMember[]>(() => {
    const rows = teamQuery.data?.rows ?? [];
    return rows.filter((row) => row.status === "active");
  }, [teamQuery.data]);

  const filtered = useMemo<TeamMember[]>(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter((row) => {
      const blob = [
        row.first_name, row.last_name, row.email, row.title,
      ].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(needle);
    });
  }, [candidates, search]);

  const onPick = (membershipId: string) => {
    if (assignedSet.has(membershipId)) return;
    setSelectedId(membershipId);
  };

  const onSubmitClick = () => {
    if (!selectedId) return;
    onSubmit({ membership_id: selectedId, is_account_lead: isLead });
  };

  const reset = () => {
    setSearch("");
    setSelectedId(null);
    setIsLead(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add team member"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="tertiary"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            data-testid="add-account-team-member-submit"
            onClick={onSubmitClick}
            disabled={saving || !selectedId}
          >
            {saving ? "Adding…" : "Add to Account team"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <input
          type="text"
          data-testid="add-account-team-member-search"
          value={search}
          placeholder="Search teammates by name, email, or title…"
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
        />
        <ul
          role="listbox"
          data-testid="add-account-team-member-list"
          className="max-h-72 overflow-y-auto rounded-md border border-border-subtle"
        >
          {teamQuery.isPending ? (
            <li className="px-3 py-2 text-xs text-text-tertiary">
              Loading…
            </li>
          ) : filtered.length === 0 ? (
            <li
              data-testid="add-account-team-member-empty"
              className="px-3 py-2 text-xs text-text-tertiary"
            >
              No matching active teammates.
            </li>
          ) : (
            filtered.map((row) => {
              const disabled = assignedSet.has(row.member_id);
              const fullName =
                [row.first_name, row.last_name].filter(Boolean).join(" ")
                || row.email;
              return (
                <li key={row.member_id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={row.member_id === selectedId}
                    aria-disabled={disabled || undefined}
                    disabled={disabled}
                    data-testid={`add-account-team-member-option-${row.member_id}`}
                    onClick={() => onPick(row.member_id)}
                    className={[
                      "flex w-full items-start gap-3 border-b border-border-subtle px-3 py-2 text-left text-xs last:border-b-0",
                      disabled
                        ? "cursor-not-allowed opacity-50"
                        : row.member_id === selectedId
                          ? "bg-sage-tint/40"
                          : "hover:bg-recessed",
                    ].join(" ")}
                  >
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium text-text-primary">
                        {fullName}
                        {row.is_producer && (
                          <span
                            data-testid={`add-account-team-member-producer-${row.member_id}`}
                            className="ml-2 inline-flex items-center rounded-full bg-gold-tint px-1.5 py-0.5 text-[10px] font-medium text-gold"
                          >
                            Producer
                          </span>
                        )}
                        {disabled && (
                          <span className="ml-2 text-[10px] font-medium uppercase tracking-wide text-text-tertiary">
                            On team
                          </span>
                        )}
                      </span>
                      <span className="text-[11px] text-text-tertiary">
                        {row.title ? `${row.title} · ` : ""}{row.email}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>

        <label className="flex flex-col gap-1 text-xs">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-text-primary">
            <input
              type="checkbox"
              data-testid="add-account-team-member-lead-toggle"
              checked={isLead}
              onChange={(e) => setIsLead(e.target.checked)}
            />
            Make Account lead
          </span>
          {isLead && hasExistingLead && (
            <span
              data-testid="add-account-team-member-lead-warning"
              className="text-text-tertiary"
            >
              This will replace the current Account lead.
            </span>
          )}
        </label>

        {error && (
          <p
            role="alert"
            data-testid="add-account-team-member-error"
            className="text-xs font-medium text-ember"
          >
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
